<template>
  <div v-if="isLoading">
    <v-skeleton-loader
      type="table-heading"
      class="mb-4"
    />
    <v-skeleton-loader type="image" />
  </div>

  <div v-else>
    <h2 class="text-h6 mb-4">
      フロアレイアウト
    </h2>

    <div
      class="d-flex align-center mb-3 flex-wrap"
      style="gap: 8px;"
    >
      <v-chip
        v-for="hall in halls"
        :key="hall.id"
        :color="getZoneColor(hall)"
        dark
        small
      >
        {{ hall.attributes.name }}
      </v-chip>
      <v-spacer />
      <div class="d-flex align-center mr-3">
        <v-btn
          small
          :outlined="!previewMode"
          :color="previewMode ? 'primary' : ''"
          @click="previewMode = !previewMode"
        >
          プレビュー
        </v-btn>
        <div
          v-if="previewMode"
          class="d-flex align-center ml-2"
          style="min-width: 140px;"
        >
          <span class="text-caption mr-2">{{ previewCastCount }}名</span>
          <v-slider
            v-model="previewCastCount"
            :min="0"
            :max="4"
            :step="1"
            hide-details
            dense
            ticks
          />
        </div>
      </div>
      <div class="d-flex align-center mr-3">
        <v-btn
          small
          outlined
          @click="zoomOut"
        >
          ー
        </v-btn>
        <span class="mx-2 text-caption">{{ Math.round(zoomLevel * 100) }}%</span>
        <v-btn
          small
          outlined
          @click="zoomIn"
        >
          ＋
        </v-btn>
      </div>
      <v-btn
        color="primary"
        :loading="isSaving"
        @click="handleSave"
      >
        保存
      </v-btn>
    </div>

    <!-- 選択中の詳細パネル（キャンバス上部に固定表示） -->
    <div
      v-if="selectedZoneIdx === null && selectedTableIdx === null"
      class="detail-panel detail-panel--empty mb-3"
    >
      エリアまたは卓を選択して編集
    </div>
    <div
      v-if="selectedZoneIdx !== null && selectedZoneData"
      class="detail-panel mb-3"
    >
      <h3 class="text-subtitle-2 mb-2 d-flex align-center">
        エリア: {{ selectedZoneData._name }}
        <v-spacer />
        <v-btn
          x-small
          color="primary"
          class="mr-2"
          @click="addSubZone(selectedZoneIdx)"
        >
          + サブエリア追加
        </v-btn>
        <v-btn
          v-if="!selectedZoneData._isPrimary"
          x-small
          outlined
          color="error"
          @click="deleteSubZone(selectedZoneIdx)"
        >
          このサブエリアを削除
        </v-btn>
      </h3>
      <v-row dense>
        <v-col cols="2">
          <v-text-field
            :value="selectedZoneData.x"
            label="X"
            type="number"
            dense
            outlined
            hide-details
            @input="updateZoneProp(selectedZoneIdx, 'x', Number($event))"
          />
        </v-col>
        <v-col cols="2">
          <v-text-field
            :value="selectedZoneData.y"
            label="Y"
            type="number"
            dense
            outlined
            hide-details
            @input="updateZoneProp(selectedZoneIdx, 'y', Number($event))"
          />
        </v-col>
        <v-col cols="2">
          <v-text-field
            :value="selectedZoneData.w"
            label="幅"
            type="number"
            dense
            outlined
            hide-details
            @input="updateZoneProp(selectedZoneIdx, 'w', Number($event))"
          />
        </v-col>
        <v-col cols="2">
          <v-text-field
            :value="selectedZoneData.h"
            label="高さ"
            type="number"
            dense
            outlined
            hide-details
            @input="updateZoneProp(selectedZoneIdx, 'h', Number($event))"
          />
        </v-col>
      </v-row>
    </div>

    <div
      v-if="selectedTableIdx !== null"
      class="detail-panel mb-3"
    >
      <h3 class="text-subtitle-2 mb-2 d-flex align-center">
        テーブル: {{ selectedTable.name || `${selectedTable._prefix}${selectedTable.table_number}${selectedTable._suffix}` }}
      </h3>
      <v-row dense>
        <v-col cols="3">
          <v-text-field
            :value="selectedTable.name || ''"
            :placeholder="`${selectedTable._prefix}${selectedTable.table_number}${selectedTable._suffix}`"
            label="卓名"
            dense
            outlined
            :counter="16"
            :maxlength="16"
            :rules="tableNameRules"
            @input="updateTableProp(selectedTableIdx, 'name', $event)"
          />
        </v-col>
        <v-col cols="2">
          <v-text-field
            :value="selectedTable.x"
            label="X"
            type="number"
            dense
            outlined
            hide-details
            @input="updateTableProp(selectedTableIdx, 'x', Number($event))"
          />
        </v-col>
        <v-col cols="2">
          <v-text-field
            :value="selectedTable.y"
            label="Y"
            type="number"
            dense
            outlined
            hide-details
            @input="updateTableProp(selectedTableIdx, 'y', Number($event))"
          />
        </v-col>
        <v-col cols="2">
          <v-text-field
            :value="selectedTable.w"
            label="幅"
            type="number"
            dense
            outlined
            hide-details
            @input="updateTableProp(selectedTableIdx, 'w', Number($event))"
          />
        </v-col>
        <v-col cols="2">
          <v-text-field
            :value="selectedTable.h"
            label="高さ"
            type="number"
            dense
            outlined
            hide-details
            @input="updateTableProp(selectedTableIdx, 'h', Number($event))"
          />
        </v-col>
      </v-row>
      <div class="mt-2 text-caption grey--text">
        ※ 卓数の変更はホール設定から行ってください
      </div>
    </div>

    <div class="floor-editor-wrapper">
      <div
        ref="canvasRef"
        class="floor-editor-canvas"
        :style="canvasStyle"
        @mouseup="onPointerUp"
        @mousemove="onPointerMove"
        @mouseleave="onPointerUp"
        @touchend="onPointerUp"
        @touchcancel="onPointerUp"
        @touchmove.prevent="onPointerMove"
        @click="onCanvasClick"
      >
        <!-- 整列ガイド -->
        <div
          v-for="(guide, gi) in alignmentGuides"
          :key="`guide-${gi}`"
          class="alignment-guide"
          :style="guideStyle(guide)"
        />
        <!-- エリア（ゾーン） -->
        <div
          v-for="(zone, zoneIdx) in allZones"
          :key="`zone-${zone._hallId}-${zone._zoneIdx}`"
          class="floor-editor-zone"
          :class="{ 'floor-editor-zone--selected': selectedZoneKey === `${zone._hallId}-${zone._zoneIdx}` }"
          :style="zoneRenderStyle(zone)"
          @mousedown.prevent="startZoneMove($event, zoneIdx)"
          @touchstart.prevent="startZoneMove($event, zoneIdx)"
          @click.stop="selectZone(zoneIdx)"
        >
          <span class="floor-editor-zone__label">{{ zone._name }}</span>
          <!-- ゾーンリサイズハンドル -->
          <div
            v-for="corner in ['tl','tr','bl','br']"
            :key="corner"
            class="zone-resize-handle"
            :class="`zone-resize-handle--${corner}`"
            @mousedown.prevent.stop="startZoneResize($event, zoneIdx, corner)"
            @touchstart.prevent.stop="startZoneResize($event, zoneIdx, corner)"
          />
        </div>

        <!-- テーブル -->
        <div
          v-for="(table, tableIdx) in allTables"
          :key="`table-${table._hallId}-${table.table_number}`"
          class="floor-editor-table"
          :class="{
            'floor-editor-table--selected': selectedTableKey === `${table._hallId}-${table.table_number}`,
            'floor-editor-table--colliding': interaction && interaction.type === 'table-move' && interaction.tableIdx === tableIdx && interaction.collides,
            'floor-editor-table--preview': previewMode,
          }"
          :style="tableStyle(table)"
          @mousedown.prevent.stop="startTableMove($event, tableIdx)"
          @touchstart.prevent.stop="startTableMove($event, tableIdx)"
          @click.stop="selectTable(tableIdx)"
        >
          <input
            v-if="editingTableIdx === tableIdx"
            ref="nameInputRef"
            :value="table.name || ''"
            :placeholder="`${table._prefix}${table.table_number}${table._suffix}`"
            class="floor-editor-table__name-input"
            @mousedown.stop
            @touchstart.stop
            @click.stop
            @blur="finishEditName"
            @keyup.enter="finishEditName"
            @input="updateTableProp(tableIdx, 'name', $event.target.value)"
          >
          <template v-else>
            <template v-if="previewMode">
              <div class="floor-editor-table__header">
                <span class="floor-editor-table__name floor-editor-table__name--preview">
                  {{ table.name || `${table._prefix}${table.table_number}${table._suffix}` }}
                </span>
                <span class="floor-editor-table__guest-badge">2名</span>
              </div>
              <div class="floor-editor-table__time">
                1:23
                <span class="floor-editor-table__remaining">/ 残30分</span>
              </div>
              <div class="floor-editor-table__total">
                ¥12,000
              </div>
            </template>
            <span
              v-else
              class="floor-editor-table__name"
            >
              {{ table.name || `${table._prefix}${table.table_number}${table._suffix}` }}
            </span>
            <button
              v-if="selectedTableKey === `${table._hallId}-${table.table_number}`"
              type="button"
              class="table-edit-name-btn"
              @click.stop="startEditName(tableIdx)"
              @mousedown.stop
              @touchstart.stop
            >
              ✎
            </button>
          </template>
          <div
            v-if="previewMode && previewCastsSplit.visible.length > 0"
            class="floor-editor-table__casts"
          >
            <span
              v-for="(cast, castIdx) in previewCastsSplit.visible"
              :key="`preview-${castIdx}`"
              class="floor-editor-table__cast-chip"
            >
              <span class="floor-editor-table__cast-type">{{ cast.type === 'nomination' ? cast.vendibleName : 'フリー' }}</span>
              {{ cast.name }}
            </span>
            <span
              v-if="previewCastsSplit.overflowCount > 0"
              class="floor-editor-table__cast-overflow"
            >+{{ previewCastsSplit.overflowCount }}名</span>
          </div>
          <div
            v-for="corner in ['tl','tr','bl','br']"
            :key="corner"
            class="table-resize-handle"
            :class="`table-resize-handle--${corner}`"
            @mousedown.prevent.stop="startTableResize($event, tableIdx, corner)"
            @touchstart.prevent.stop="startTableResize($event, tableIdx, corner)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ref,
  computed,
  getCurrentInstance,
} from '@vue/composition-api'
import { cloneDeep } from 'lodash'
import HallApi from '@/api/waiter/Hall'
import useCompInit from '@/views/composable/useCompInit'
import {
  fillMissingTableLayouts,
  expandZoneToFitLayouts,
  predictExpandedZoneSize,
  flattenHallZones,
  filterLayoutsOutsideZones,
  findLastZoneFlatIdx,
} from './floorLayoutUtils'
import { generateDummyCasts, splitCastsForDisplay } from '../floorUtils'

const ZONE_COLORS = ['#10b981', '#8b5cf6', '#3b82f6', '#f59e0b', '#ef4444', '#ec4899']
const DEFAULT_TABLE = { w: 160, h: 130 }
const MIN_SIZE = 60
const SNAP_THRESHOLD = 8

export default {
  setup() {
    const vm = getCurrentInstance().proxy
    const { isLoading, initWith } = useCompInit()
    const halls = ref([])
    const isSaving = ref(false)
    const canvasRef = ref(null)
    const interaction = ref(null)
    const selectedZoneIdx = ref(null)
    const selectedTableIdx = ref(null)
    const alignmentGuides = ref([])
    const editingTableIdx = ref(null)
    const nameInputRef = ref(null)
    const savedZoom = localStorage.getItem('floorLayoutZoomLevel')
    const zoomLevel = ref(savedZoom ? Number(savedZoom) : 1)

    // 運用時プレビュー: ダミーのキャストを卓に注入して見え方を確認
    const previewMode = ref(false)
    const previewCastCount = ref(2)
    const previewCastsSplit = computed(() => splitCastsForDisplay(generateDummyCasts(previewCastCount.value), 2))

    const saveZoom = () => {
      localStorage.setItem('floorLayoutZoomLevel', String(zoomLevel.value))
    }
    const zoomIn = () => {
      zoomLevel.value = Math.min(2, Math.round((zoomLevel.value + 0.05) * 20) / 20)
      saveZoom()
    }
    const zoomOut = () => {
      zoomLevel.value = Math.max(0.3, Math.round((zoomLevel.value - 0.05) * 20) / 20)
      saveZoom()
    }

    const startEditName = idx => {
      editingTableIdx.value = idx
      // 次のtickでフォーカス
      setTimeout(() => {
        const el = nameInputRef.value
        const input = Array.isArray(el) ? el[0] : el
        if (input) {
          input.focus()
          input.select()
        }
      }, 0)
    }

    const finishEditName = () => {
      editingTableIdx.value = null
    }

    // ── ゾーンデータ ──
    // 新規ゾーンは placeholder で生成し、位置/サイズは getHalls の縦配置パスで設定する。
    // 色のみ常に保証。
    const ensureZone = hallIdx => {
      const hall = halls.value[hallIdx]
      if (!hall.attributes.floorZone || typeof hall.attributes.floorZone !== 'object' || Array.isArray(hall.attributes.floorZone)) {
        hall.attributes.floorZone = {
          x: 0, y: 0, w: 0, h: 0,
        }
      }
      if (!hall.attributes.floorZone.color || hall.attributes.floorZone.color === '#334155') {
        hall.attributes.floorZone = {
          ...hall.attributes.floorZone,
          color: ZONE_COLORS[hallIdx % ZONE_COLORS.length],
        }
      }

      return hall.attributes.floorZone
    }

    const getHalls = async () => {
      const res = await HallApi.getHalls({ isSettings: true })
      if (res?.data) {
        halls.value = cloneDeep(res.data.halls.data)
        // tableCount に対して未配置の卓を自動補完（フロアレイアウト＝全卓表示）
        // 新規ゾーンは縦に積んで重ならないよう配置する
        const ZONE_GAP = 20
        // キャンバス左上からのデフォルト余白
        const CANVAS_MARGIN_X = 20
        const CANVAS_MARGIN_Y = 20
        let nextY = CANVAS_MARGIN_Y
        for (let hallIdx = 0; hallIdx < halls.value.length; hallIdx += 1) {
          const hall = halls.value[hallIdx]
          const fz = hall.attributes.floorZone
          const wasFresh = !fz || typeof fz !== 'object' || Array.isArray(fz)

          ensureZone(hallIdx)

          // 新規ゾーンの場合: 予測サイズで仮配置（その後 fill+expand を通す）
          if (wasFresh) {
            const predicted = predictExpandedZoneSize(hall.attributes.tableCount || 0, DEFAULT_TABLE)
            hall.attributes.floorZone = {
              ...hall.attributes.floorZone,
              x: CANVAS_MARGIN_X,
              y: nextY,
              w: predicted.w,
              h: predicted.h,
            }
          }

          const zone = hall.attributes.floorZone
          const newLayouts = fillMissingTableLayouts(
            hall.attributes.tableLayouts || [],
            hall.attributes.tableCount || 0,
            zone,
            DEFAULT_TABLE,
          )
          hall.attributes.tableLayouts = newLayouts
          // プライマリゾーンの拡張時は、サブエリアに移動された卓は除外する
          // （除外しないとプライマリがサブエリアと重なってしまう）
          const subs = (zone && zone.sub_zones) || []
          const layoutsForPrimary = filterLayoutsOutsideZones(newLayouts, subs)
          hall.attributes.floorZone = expandZoneToFitLayouts(zone, layoutsForPrimary)

          // 次のゾーンの開始 Y を更新（既存ゾーンの底辺も考慮）
          const finalZone = hall.attributes.floorZone
          nextY = Math.max(nextY, (finalZone.y || 0) + (finalZone.h || 0) + ZONE_GAP)
        }
      }
    }

    initWith([getHalls()])

    // halls.value への ensureZone 適用は getHalls 内で完了している前提。
    // computed は読み取り専用 (副作用なし) にする。
    const allZones = computed(() => flattenHallZones(halls.value, ZONE_COLORS))

    const selectedZoneKey = computed(() => {
      if (selectedZoneIdx.value === null) return null
      const z = allZones.value[selectedZoneIdx.value]

      return z ? `${z._hallId}-${z._zoneIdx}` : null
    })

    const selectedZoneData = computed(() => {
      if (selectedZoneIdx.value === null) return null

      return allZones.value[selectedZoneIdx.value] || null
    })

    const getZoneColor = hall => {
      if (hall.attributes.floorZone && !Array.isArray(hall.attributes.floorZone)) {
        return hall.attributes.floorZone.color || '#334155'
      }

      return '#334155'
    }

    const zoneRenderStyle = zone => {
      const color = zone.color || ZONE_COLORS[0]

      return {
        position: 'absolute',
        left: `${zone.x}px`,
        top: `${zone.y}px`,
        width: `${zone.w}px`,
        height: `${zone.h}px`,
        background: `${color}30`,
        border: `2px solid ${color}`,
        borderRadius: '8px',
      }
    }

    const updateZoneProp = (flatIdx, key, val) => {
      const z = allZones.value[flatIdx]
      if (!z) return
      const hall = halls.value[z._hallIdx]
      const fz = hall.attributes.floorZone
      if (z._isPrimary) {
        hall.attributes.floorZone = { ...fz, [key]: val }
      } else {
        const newSubs = [...(fz.sub_zones || [])]
        newSubs[z._subIdx] = { ...newSubs[z._subIdx], [key]: val }
        hall.attributes.floorZone = { ...fz, sub_zones: newSubs }
      }
    }

    // ── サブゾーン追加・削除 ──
    const addSubZone = flatIdx => {
      const z = allZones.value[flatIdx]
      if (!z) return
      const hall = halls.value[z._hallIdx]
      const fz = hall.attributes.floorZone
      const subs = [...(fz.sub_zones || [])]
      // 直近のゾーン（プライマリ または 最後のサブ）の右に配置
      const primaryRect = {
        x: fz.x, y: fz.y, w: fz.w, h: fz.h,
      }
      const allHallZones = [primaryRect, ...subs]
      const last = allHallZones[allHallZones.length - 1]
      subs.push({
        x: (last.x || 0) + (last.w || 0) + 20,
        y: last.y || 0,
        w: 300,
        h: 250,
      })
      hall.attributes.floorZone = { ...fz, sub_zones: subs }
      // 追加したサブを選択する。allZones (computed) の再評価タイミング依存を避け、
      // 最新の halls から直接フラット idx を計算する。
      selectedZoneIdx.value = findLastZoneFlatIdx(halls.value, hall.id)
    }

    const deleteSubZone = flatIdx => {
      const z = allZones.value[flatIdx]
      if (!z || z._isPrimary) return
      const hall = halls.value[z._hallIdx]
      const fz = hall.attributes.floorZone
      const newSubs = [...(fz.sub_zones || [])]
      newSubs.splice(z._subIdx, 1)
      hall.attributes.floorZone = { ...fz, sub_zones: newSubs }
      selectedZoneIdx.value = null
    }

    // ── テーブルデータ ──
    const allTables = computed(() => {
      const result = []
      halls.value.forEach(hall => {
        const layouts = hall.attributes.tableLayouts || []
        layouts.forEach((layout, idx) => {
          const hallIdx = halls.value.indexOf(hall)
          result.push({
            ...layout,
            _hallId: hall.id,
            _hallIdx: hallIdx,
            _layoutIdx: idx,
            _prefix: hall.attributes.tablePrefix || '',
            _suffix: hall.attributes.tableSuffix || '卓',
            _color: hall.attributes.floorZone?.color || ZONE_COLORS[hallIdx % ZONE_COLORS.length],
          })
        })
      })

      return result
    })

    const selectedTableKey = computed(() => {
      if (selectedTableIdx.value === null) return null
      const t = allTables.value[selectedTableIdx.value]
      if (!t) return null

      return `${t._hallId}-${t.table_number}`
    })

    const selectedTable = computed(() => {
      if (selectedTableIdx.value === null) return null

      return allTables.value[selectedTableIdx.value] || null
    })

    // コンテンツ最大範囲からキャンバスサイズを計算
    const CANVAS_MIN_W = 1200
    const CANVAS_MIN_H = 800
    const CANVAS_PADDING = 60

    const canvasContentSize = computed(() => {
      let maxX = 0
      let maxY = 0
      allZones.value.forEach(z => {
        maxX = Math.max(maxX, (z.x || 0) + (z.w || 0))
        maxY = Math.max(maxY, (z.y || 0) + (z.h || 0))
      })
      allTables.value.forEach(t => {
        maxX = Math.max(maxX, (t.x || 0) + (t.w || 0))
        maxY = Math.max(maxY, (t.y || 0) + (t.h || 0))
      })
      // 編集時の操作余白として少し追加
      maxX += CANVAS_PADDING
      maxY += CANVAS_PADDING
      if (maxX <= CANVAS_PADDING) maxX = CANVAS_MIN_W
      if (maxY <= CANVAS_PADDING) maxY = CANVAS_MIN_H

      return { w: maxX, h: maxY }
    })

    const canvasStyle = computed(() => ({
      width: `${canvasContentSize.value.w}px`,
      height: `${canvasContentSize.value.h}px`,
      transform: `scale(${zoomLevel.value})`,
      transformOrigin: 'top left',
      // scale後の実サイズ分だけ余白を確保するため、margin-rightで余分を相殺
      marginRight: `${canvasContentSize.value.w * (zoomLevel.value - 1)}px`,
      marginBottom: `${canvasContentSize.value.h * (zoomLevel.value - 1)}px`,
    }))

    const tableStyle = table => ({
      position: 'absolute',
      left: `${table.x}px`,
      top: `${table.y}px`,
      width: `${table.w || DEFAULT_TABLE.w}px`,
      height: `${table.h || DEFAULT_TABLE.h}px`,
      borderColor: `${table._color}88`,
      background: `${table._color}15`,
    })

    const TABLE_NAME_MAX_LENGTH = 16
    const tableNameRules = [
      v => !v || v.length <= TABLE_NAME_MAX_LENGTH || `卓名は${TABLE_NAME_MAX_LENGTH}文字以内で入力してください`,
      v => !v || !/[\x00-\x1F\x7F]/.test(v) || '改行・制御文字は使用できません', // eslint-disable-line no-control-regex
    ]

    const updateTableProp = (tableIdx, key, val) => {
      const t = allTables.value[tableIdx]
      if (!t) return
      const hall = halls.value[t._hallIdx]
      const layouts = [...hall.attributes.tableLayouts]
      const next = { ...layouts[t._layoutIdx] }
      if (key === 'name') {
        const trimmed = (val || '').slice(0, TABLE_NAME_MAX_LENGTH)
        if (trimmed === '') {
          delete next.name
        } else {
          next.name = trimmed
        }
      } else {
        next[key] = val
      }
      layouts[t._layoutIdx] = next
      hall.attributes.tableLayouts = layouts
    }

    // 矩形の重なり判定
    const isOverlap = (a, b) => (
      a.x < b.x + b.w
      && a.x + a.w > b.x
      && a.y < b.y + b.h
      && a.y + a.h > b.y
    )

    const hasCollision = (target, others) => others.some(o => isOverlap(target, o))

    // ── ドラッグ ──
    const getCanvasPos = e => {
      const rect = canvasRef.value.getBoundingClientRect()
      const touch = e.touches ? e.touches[0] : e

      // scale分を補正
      return {
        x: (touch.clientX - rect.left) / zoomLevel.value,
        y: (touch.clientY - rect.top) / zoomLevel.value,
      }
    }

    const selectZone = hallIdx => {
      selectedZoneIdx.value = hallIdx
      selectedTableIdx.value = null
    }

    const selectTable = tableIdx => {
      selectedTableIdx.value = tableIdx
      selectedZoneIdx.value = null
    }

    const onCanvasClick = () => {
      selectedZoneIdx.value = null
      selectedTableIdx.value = null
    }

    const startZoneMove = (e, flatIdx) => {
      selectZone(flatIdx)
      const pos = getCanvasPos(e)
      const zone = allZones.value[flatIdx]
      interaction.value = {
        type: 'zone-move',
        flatIdx,
        offsetX: pos.x - (zone.x || 0),
        offsetY: pos.y - (zone.y || 0),
      }
    }

    const startZoneResize = (e, flatIdx, corner) => {
      selectZone(flatIdx)
      const pos = getCanvasPos(e)
      const zone = allZones.value[flatIdx]
      interaction.value = {
        type: 'zone-resize',
        flatIdx,
        corner,
        startX: pos.x,
        startY: pos.y,
        origX: zone.x || 0,
        origY: zone.y || 0,
        startW: zone.w || 400,
        startH: zone.h || 350,
      }
    }

    const startTableMove = (e, tableIdx) => {
      selectTable(tableIdx)
      const t = allTables.value[tableIdx]
      const pos = getCanvasPos(e)
      interaction.value = {
        type: 'table-move',
        tableIdx,
        offsetX: pos.x - (t.x || 0),
        offsetY: pos.y - (t.y || 0),
        lastValidX: t.x || 0,
        lastValidY: t.y || 0,
      }
    }

    const startTableResize = (e, tableIdx, corner) => {
      selectTable(tableIdx)
      const t = allTables.value[tableIdx]
      const pos = getCanvasPos(e)
      interaction.value = {
        type: 'table-resize',
        tableIdx,
        corner,
        startX: pos.x,
        startY: pos.y,
        origX: t.x || 0,
        origY: t.y || 0,
        startW: t.w || DEFAULT_TABLE.w,
        startH: t.h || DEFAULT_TABLE.h,
      }
    }

    // 移動・リサイズ中の他の卓と整列するためのスナップ計算
    const computeSnap = (target, others) => {
      const guides = []
      let snapX = target.x
      let snapY = target.y
      const tCx = target.x + target.w / 2
      const tCy = target.y + target.h / 2
      const tR = target.x + target.w
      const tB = target.y + target.h

      let bestDx = SNAP_THRESHOLD + 1
      let bestDy = SNAP_THRESHOLD + 1
      let bestVLine = null
      let bestHLine = null

      others.forEach(o => {
        const oCx = o.x + o.w / 2
        const oCy = o.y + o.h / 2
        const oR = o.x + o.w
        const oB = o.y + o.h

        const xCands = [
          { v: o.x - target.x, line: o.x },
          { v: oCx - tCx, line: oCx },
          { v: oR - tR, line: oR },
          { v: o.x - tR, line: o.x },
          { v: oR - target.x, line: oR },
        ]
        xCands.forEach(c => {
          if (Math.abs(c.v) < Math.abs(bestDx)) {
            bestDx = c.v
            bestVLine = c.line
          }
        })

        const yCands = [
          { v: o.y - target.y, line: o.y },
          { v: oCy - tCy, line: oCy },
          { v: oB - tB, line: oB },
          { v: o.y - tB, line: o.y },
          { v: oB - target.y, line: oB },
        ]
        yCands.forEach(c => {
          if (Math.abs(c.v) < Math.abs(bestDy)) {
            bestDy = c.v
            bestHLine = c.line
          }
        })
      })

      if (Math.abs(bestDx) <= SNAP_THRESHOLD) {
        snapX = target.x + bestDx
        guides.push({ dir: 'v', pos: bestVLine })
      }
      if (Math.abs(bestDy) <= SNAP_THRESHOLD) {
        snapY = target.y + bestDy
        guides.push({ dir: 'h', pos: bestHLine })
      }

      return { x: snapX, y: snapY, guides }
    }

    const guideStyle = guide => {
      if (guide.dir === 'v') {
        return {
          left: `${guide.pos}px`, top: '0', width: '1px', height: '100%',
        }
      }

      return {
        top: `${guide.pos}px`, left: '0', height: '1px', width: '100%',
      }
    }

    const onPointerMove = e => {
      if (!interaction.value) return
      const pos = getCanvasPos(e)
      const { type } = interaction.value

      if (type === 'zone-move') {
        const z = allZones.value[interaction.value.flatIdx]
        if (!z) return
        let nx = Math.max(0, Math.round(pos.x - interaction.value.offsetX))
        let ny = Math.max(0, Math.round(pos.y - interaction.value.offsetY))
        const others = allZones.value
          .filter((_, i) => i !== interaction.value.flatIdx)
          .map(o => ({
            x: o.x || 0, y: o.y || 0, w: o.w, h: o.h,
          }))
        const snap = computeSnap({
          x: nx, y: ny, w: z.w, h: z.h,
        }, others)
        nx = snap.x
        ny = snap.y
        alignmentGuides.value = snap.guides
        updateZoneProp(interaction.value.flatIdx, 'x', nx)
        updateZoneProp(interaction.value.flatIdx, 'y', ny)
      } else if (type === 'zone-resize') {
        const {
          flatIdx, corner, startX, startY, origX, origY, startW, startH,
        } = interaction.value
        const dx = pos.x - startX
        const dy = pos.y - startY
        const updates = {}

        if (corner.includes('r')) updates.w = Math.max(MIN_SIZE, Math.round(startW + dx))
        if (corner.includes('b')) updates.h = Math.max(MIN_SIZE, Math.round(startH + dy))
        if (corner.includes('l')) {
          const d = Math.min(dx, startW - MIN_SIZE)
          updates.x = Math.round(origX + d)
          updates.w = Math.max(MIN_SIZE, Math.round(startW - d))
        }
        if (corner.includes('t')) {
          const d = Math.min(dy, startH - MIN_SIZE)
          updates.y = Math.round(origY + d)
          updates.h = Math.max(MIN_SIZE, Math.round(startH - d))
        }

        // 他ゾーンと幅/高さを揃える
        allZones.value.forEach((o, i) => {
          if (i === flatIdx) return
          if (updates.w !== undefined && Math.abs(o.w - updates.w) <= SNAP_THRESHOLD) updates.w = o.w
          if (updates.h !== undefined && Math.abs(o.h - updates.h) <= SNAP_THRESHOLD) updates.h = o.h
        })

        Object.entries(updates).forEach(([k, v]) => updateZoneProp(flatIdx, k, v))
      } else if (type === 'table-move') {
        const t = allTables.value[interaction.value.tableIdx]
        if (!t) return
        const hall = halls.value[t._hallIdx]
        let nx = Math.max(0, Math.round(pos.x - interaction.value.offsetX))
        let ny = Math.max(0, Math.round(pos.y - interaction.value.offsetY))
        const others = allTables.value
          .filter((_, i) => i !== interaction.value.tableIdx)
          .map(o => ({
            x: o.x, y: o.y, w: o.w, h: o.h,
          }))
        const snap = computeSnap({
          x: nx, y: ny, w: t.w, h: t.h,
        }, others)
        nx = snap.x
        ny = snap.y
        alignmentGuides.value = snap.guides
        const collides = hasCollision({
          x: nx, y: ny, w: t.w, h: t.h,
        }, others)
        interaction.value.collides = collides
        // 重ならない位置は記憶しておく
        if (!collides) {
          interaction.value.lastValidX = nx
          interaction.value.lastValidY = ny
        }
        // ドラッグ中は重なってもOK（視覚的に移動はする）
        const layouts = [...hall.attributes.tableLayouts]
        layouts[t._layoutIdx] = { ...layouts[t._layoutIdx], x: nx, y: ny }
        hall.attributes.tableLayouts = layouts
      } else if (type === 'table-resize') {
        const t = allTables.value[interaction.value.tableIdx]
        if (!t) return
        const hall = halls.value[t._hallIdx]
        const {
          corner, startX, startY, origX, origY, startW, startH,
        } = interaction.value
        const dx = pos.x - startX
        const dy = pos.y - startY
        const update = { ...hall.attributes.tableLayouts[t._layoutIdx] }

        if (corner.includes('r')) update.w = Math.max(MIN_SIZE, Math.round(startW + dx))
        if (corner.includes('b')) update.h = Math.max(MIN_SIZE, Math.round(startH + dy))
        if (corner.includes('l')) {
          const d = Math.min(dx, startW - MIN_SIZE)
          update.x = Math.round(origX + d)
          update.w = Math.max(MIN_SIZE, Math.round(startW - d))
        }
        if (corner.includes('t')) {
          const d = Math.min(dy, startH - MIN_SIZE)
          update.y = Math.round(origY + d)
          update.h = Math.max(MIN_SIZE, Math.round(startH - d))
        }

        // 他卓と幅/高さを揃える
        const others = []
        allTables.value.forEach((o, i) => {
          if (i === interaction.value.tableIdx) return
          if (Math.abs(o.w - update.w) <= SNAP_THRESHOLD) update.w = o.w
          if (Math.abs(o.h - update.h) <= SNAP_THRESHOLD) update.h = o.h
          others.push({
            x: o.x, y: o.y, w: o.w, h: o.h,
          })
        })

        // 重なる場合はリサイズを適用しない
        if (hasCollision(update, others)) return

        const layouts = [...hall.attributes.tableLayouts]
        layouts[t._layoutIdx] = update
        hall.attributes.tableLayouts = layouts
      }
    }

    const onPointerUp = () => {
      // table-moveで重なったまま離した場合、最後の有効位置に戻す
      if (interaction.value && interaction.value.type === 'table-move' && interaction.value.collides) {
        const t = allTables.value[interaction.value.tableIdx]
        if (t) {
          const hall = halls.value[t._hallIdx]
          const layouts = [...hall.attributes.tableLayouts]
          layouts[t._layoutIdx] = {
            ...layouts[t._layoutIdx],
            x: interaction.value.lastValidX,
            y: interaction.value.lastValidY,
          }
          hall.attributes.tableLayouts = layouts
        }
      }
      interaction.value = null
      alignmentGuides.value = []
    }

    // ── 保存 ──
    // NOTE: Backend に bulk update API がないため、ホール単位で並列に updateHall を
    // 呼んでいる。途中で失敗しても他の成功は反映されてしまうため、Promise.allSettled
    // で個別に成否を追跡し、ユーザーには「成功した分」「失敗した分」を区別して通知する。
    const handleSave = async () => {
      isSaving.value = true
      try {
        const targets = halls.value.map(hall => ({
          hallName: hall.attributes.name,
          promise: HallApi.updateHall({
            hallId: hall.id,
            params: {
              tableLayouts: JSON.stringify(hall.attributes.tableLayouts || []),
              floorZone: JSON.stringify(hall.attributes.floorZone || null),
              // table_init_config_attributesが必須なので既存値を送る
              tableInitConfigAttributes: { id: hall.attributes.tableInitConfig?.data?.id || '' },
            },
          }),
        }))
        const results = await Promise.allSettled(targets.map(t => t.promise))
        const failed = results
          .map((r, i) => (r.status === 'rejected' ? targets[i].hallName : null))
          .filter(name => name !== null)

        if (failed.length === 0) {
          vm.$toast.success('フロアレイアウトを保存しました')
        } else if (failed.length === targets.length) {
          vm.$toast.error('保存に失敗しました')
        } else {
          // 部分保存: 成功と失敗を明示的に区別する
          vm.$toast.warning(`一部のホール (${failed.join(', ')}) の保存に失敗しました。再度お試しください。`)
        }
      } catch (error) {
        console.error('Save failed:', error)
        vm.$toast.error('保存に失敗しました')
      }
      isSaving.value = false
    }

    return {
      isLoading,
      halls,
      isSaving,
      previewMode,
      previewCastCount,
      previewCastsSplit,
      canvasRef,
      alignmentGuides,
      guideStyle,
      editingTableIdx,
      nameInputRef,
      startEditName,
      finishEditName,
      interaction,
      zoomLevel,
      zoomIn,
      zoomOut,
      canvasStyle,
      selectedZoneIdx,
      selectedZoneKey,
      selectedZoneData,
      selectedTableIdx,
      selectedTableKey,
      selectedTable,
      allZones,
      allTables,
      getZoneColor,
      zoneRenderStyle,
      updateZoneProp,
      tableStyle,
      updateTableProp,
      tableNameRules,
      addSubZone,
      deleteSubZone,
      selectZone,
      selectTable,
      onCanvasClick,
      startZoneMove,
      startZoneResize,
      startTableMove,
      startTableResize,
      onPointerMove,
      onPointerUp,
      handleSave,
    }
  },
}
</script>

<style scoped>
.floor-editor-wrapper {
  overflow: auto;
  max-height: calc(100vh - 220px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: #1a1a2e;
}

.floor-editor-canvas {
  position: relative;
  background-image:
    radial-gradient(circle, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
  touch-action: none;
}

.alignment-guide {
  position: absolute;
  background: #ec4899;
  pointer-events: none;
  z-index: 100;
  box-shadow: 0 0 4px rgba(236, 72, 153, 0.6);
}

.detail-panel {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 12px;
  min-height: 132px;
}

.floor-editor-table--colliding {
  background: rgba(239, 68, 68, 0.25) !important;
  border-color: #ef4444 !important;
  box-shadow: 0 0 8px rgba(239, 68, 68, 0.6);
}

.detail-panel--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 13px;
  border-style: dashed;
  border-color: rgba(100, 116, 139, 0.3);
}

.floor-editor-zone {
  cursor: grab;
  user-select: none;
  z-index: 1;
}

.floor-editor-zone:active {
  cursor: grabbing;
}

.floor-editor-zone--selected {
  border-style: solid !important;
  border-width: 2px !important;
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.15);
}

.floor-editor-zone__label {
  position: absolute;
  top: 6px;
  left: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #e2e8f0;
}

.zone-resize-handle {
  position: absolute;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #f59e0b;
  border: 2px solid #1a1a2e;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 10;
}

.floor-editor-zone--selected .zone-resize-handle,
.floor-editor-zone:hover .zone-resize-handle {
  opacity: 1;
}

.zone-resize-handle--tl { top: -8px; left: -8px; cursor: nwse-resize; }
.zone-resize-handle--tr { top: -8px; right: -8px; cursor: nesw-resize; }
.zone-resize-handle--bl { bottom: -8px; left: -8px; cursor: nesw-resize; }
.zone-resize-handle--br { bottom: -8px; right: -8px; cursor: nwse-resize; }

.floor-editor-table {
  background: #1e293b;
  border: 2px solid #475569;
  border-radius: 8px;
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  z-index: 2;
}

.floor-editor-table:active {
  cursor: grabbing;
}

.floor-editor-table--selected {
  border-color: #fff;
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.2);
}

.table-resize-handle {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #3b82f6;
  border: 2px solid #1a1a2e;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 10;
}

.floor-editor-table--selected .table-resize-handle,
.floor-editor-table:hover .table-resize-handle {
  opacity: 1;
}

.table-resize-handle--tl { top: -6px; left: -6px; cursor: nwse-resize; }
.table-resize-handle--tr { top: -6px; right: -6px; cursor: nesw-resize; }
.table-resize-handle--bl { bottom: -6px; left: -6px; cursor: nesw-resize; }
.table-resize-handle--br { bottom: -6px; right: -6px; cursor: nwse-resize; }

.floor-editor-table--preview {
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  padding: 8px;
}

.floor-editor-table__name {
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  text-align: center;
  padding: 4px;
  cursor: text;
}

.floor-editor-table__name--preview {
  font-size: 13px;
  color: #fff;
  text-align: left;
  padding: 0;
}

.floor-editor-table__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
  pointer-events: none;
}

.floor-editor-table__guest-badge {
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  color: #e2e8f0;
  font-weight: 600;
}

.floor-editor-table__time {
  font-size: 18px;
  font-weight: 700;
  color: #6ee7b7;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  pointer-events: none;
}

.floor-editor-table__remaining {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
}

.floor-editor-table__total {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  pointer-events: none;
}

.floor-editor-table__casts {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  pointer-events: none;
  margin-top: 2px;
}

.floor-editor-table__cast-chip {
  font-size: 10px;
  padding: 2px 3px 2px 6px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border-left: 3px solid #64748b;
}

.floor-editor-table__cast-type {
  font-size: 8px;
  font-weight: 700;
  opacity: 0.6;
}

.floor-editor-table__cast-overflow {
  font-size: 10px;
  font-weight: 700;
  color: #e2e8f0;
  background: rgba(148, 163, 184, 0.2);
  border-radius: 4px;
  padding: 0 6px;
  min-height: 20px;
  display: inline-flex;
  align-items: center;
}

.floor-editor-table__name-input {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  text-align: center;
  background: rgba(99, 102, 241, 0.3);
  border: 1px solid #6366f1;
  border-radius: 4px;
  padding: 2px 4px;
  width: 80%;
  outline: none;
}

.table-edit-name-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.85);
  color: #fff;
  font-size: 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  z-index: 4;
}

.table-edit-name-btn:hover {
  background: rgba(99, 102, 241, 1);
}

/* ========================================================================
 * ライトモードオーバーライド
 * Vuetify2は v-application に .theme--light / .theme--dark を付けるため、
 * 祖先セレクタで現在のテーマを判定できる。Floor.vue のライトモード調整に合わせる。
 * ====================================================================== */

/* キャンバス */
.theme--light .floor-editor-wrapper {
  background: #fafafa;
  border-color: rgba(0, 0, 0, 0.12);
}

.theme--light .floor-editor-canvas {
  background-image:
    radial-gradient(circle, rgba(0, 0, 0, 0.08) 1px, transparent 1px);
}

/* ゾーン */
.theme--light .floor-editor-zone__label {
  color: rgba(0, 0, 0, 0.6);
}

.theme--light .floor-editor-zone--selected {
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.15);
}

.theme--light .zone-resize-handle {
  border-color: #ffffff;
}

/* 詳細パネル */
.theme--light .detail-panel {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.12);
}

.theme--light .detail-panel--empty {
  color: rgba(0, 0, 0, 0.54);
  border-color: rgba(0, 0, 0, 0.18);
}

/* テーブル本体 */
.theme--light .floor-editor-table {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.3);
}

.theme--light .floor-editor-table--selected {
  border-color: #1d4ed8;
  box-shadow: 0 0 12px rgba(29, 78, 216, 0.2);
}

.theme--light .table-resize-handle {
  border-color: #ffffff;
}

/* テーブル内文字 */
.theme--light .floor-editor-table__name {
  color: rgba(0, 0, 0, 0.6);
}

.theme--light .floor-editor-table__name--preview {
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .floor-editor-table__guest-badge {
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.7);
}

.theme--light .floor-editor-table__time {
  color: #059669;
}

.theme--light .floor-editor-table__remaining,
.theme--light .floor-editor-table__total {
  color: rgba(0, 0, 0, 0.6);
}

/* キャストチップ */
.theme--light .floor-editor-table__cast-chip {
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .floor-editor-table__cast-overflow {
  background: rgba(0, 0, 0, 0.08);
  color: rgba(0, 0, 0, 0.87);
}

/* 名前編集 */
.theme--light .floor-editor-table__name-input {
  color: rgba(0, 0, 0, 0.87);
  background: rgba(99, 102, 241, 0.08);
}
</style>
